import React from 'react';
import {
  RiskSymbolCell,
  ActionLinkCell,
  TagsCell,
  TreatmentProductsCell,
  SimpleTextCell,
  ActivityTypesCell,
} from '@client/components/ui/table/TableCells';
import TruncatedText from '@client/components/ui/TruncatedText';

export const createExpandedRowRender = () => (record) => {
  const uniqueItems = new Map();

  const addUnique = (id, name) => {
    if (name && !uniqueItems.has(id)) {
      uniqueItems.set(id, name);
    }
  };

  record.treatment_cultures?.forEach((treatmentCulture) => {
    if (treatmentCulture.culture) {
      addUnique(
        treatmentCulture.culture.id,
        treatmentCulture.culture.french_name
      );
    }
    treatmentCulture.enemies?.forEach((enemy) => {
      addUnique(enemy.id, enemy.display_name);
    });
  });

  const tagGroups = Array.from(uniqueItems.entries())
    .slice(0, 20)
    .map(([id, name]) => ({
      key: id,
      name: name,
    }));

  return (
    <div className="expanded-row">
      <TagsCell
        items={tagGroups}
        getItemName={(item) => item.name}
        getItemKey={(item) => item.key}
      />
    </div>
  );
};

export const createTableColumns = (t, currentSorting, searchData) => {
  // Get treatment type label for dynamic column header
  const treatmentTypeLabel = searchData?.selectionLabels?.treatment_type?.[0]?.label?.toLowerCase() || '';

  // Determine which header to use based on treatment type
  const getActivityTypesTitle = () => {
    if (treatmentTypeLabel.includes('fongicide')) {
      return t('treatment-search|table.activity_types_fongicide');
    }
    if (treatmentTypeLabel.includes('insecticide')) {
      return t('treatment-search|table.activity_types_insecticide');
    }
    return t('treatment-search|table.activity_types_generic');
  };

  return [
  {
    title: t('treatment-search|table.commercial_product_active_substance'),
    dataIndex: 'name',
    key: 'name',
    render: (text, record) => (
      <div className="flex flex-col gap-2 justify-start items-start">
        {record?.treatment_products?.length > 0 && (
          <>
            {TreatmentProductsCell({
              TreatmentProducts: record.treatment_products,
            })}
          </>
        )}
      </div>
    ),
    sorter: true,
    sortOrder: currentSorting?.field === 'name' ? currentSorting.order : null,
    onHeaderCell: () => ({
      style: {
        height: 'auto',
        verticalAlign: 'top',
        padding: '8px',
      },
    }),
  },
  {
    title: t('treatment-search|table.rain_delay'),
    key: 'delay_before_rain',
    render: (_, record) => (
      <SimpleTextCell text={record?.delay_before_rain} />
    ),
    width: 60,
    onHeaderCell: () => ({
      style: {
        height: 'auto',
        verticalAlign: 'top',
        padding: '8px',
      },
    }),
  },
  {
    title: t('treatment-search|table.Intervention period'),
    key: 'intervention_period',
    render: (_, record) => (
      <TruncatedText
        text={
          record?.intervention_period?.display_name ||
          record?.intervention_period?.name
        }
        maxLength={40}
      />
    ),
    width: 60,
    onHeaderCell: () => ({
      style: {
        height: 'auto',
        verticalAlign: 'top',
        padding: '8px',
      },
    }),
  },
  {
    title: t('treatment-search|table.Security delay'),
    key: 'security_delays_combined',
    render: (_, record) => {
      console.log('record', record?.security_delays_combined);
      return <SimpleTextCell text={record?.security_delays_combined} />;
    },
    width: 60,
  },
  {
    title: t('treatment-search|table.DAAR'),
    dataIndex: 'delays_before_harvest_combined',
    key: 'delays_before_harvest_combined',
    render: (_, record) => {
      return <SimpleTextCell text={record?.delays_before_harvest_combined} />;
    },
    sorter: false,
    width: 60,
  },
  {
    title: getActivityTypesTitle(),
    key: 'activity_types',
    render: (_, record) => (
      <ActivityTypesCell treatmentProducts={record.treatment_products} />
    ),
    width: 60,
  },
  {
    title: t('treatment-search|table.risks_on_health'),
    children: [
      {
        title: t('treatment-search|table.mammals_including_humans'),
        children: [
          {
            title: t('IRS'),
            key: 'health_risk_index',
            className: 'vertical-cell',
            render: (_, record) => (
              <SimpleTextCell text={record?.health_risk_index} />
            ),
            width: 20,
          },
          {
            title: t('treatment-search|table.acute_toxicity'),
            key: 'acute_toxicity',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.health?.acute_toxicity_symbol}
                alt="Acute toxicity"
              />
            ),
            width: 20,
          },
          {
            title: t('treatment-search|table.long_term_effects'),
            key: 'long_term_effects',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.health?.long_term_effect_symbol}
                alt="Long term effects"
              />
            ),
            width: 40,
          },
        ],
      },
    ],
  },
  {
    title: t('treatment-search|table.risks_on_environment'),
    children: [
      {
        title: t('treatment-search|table.non_target_species'),
        children: [
          {
            title: t('IRE'),
            key: 'environment_risk_index',
            className: 'vertical-cell',
            render: (_, record) => (
              <SimpleTextCell text={record?.environment_risk_index} />
            ),
            width: 20,
          },
          {
            title: t('treatment-search|table.fish_or_daphnia'),
            key: 'fish_daphnia',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.fishes_symbol}
                alt="Fish or daphnia"
              />
            ),
            width: 40,
          },
          {
            title: t('treatment-search|table.birds'),
            key: 'birds',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.birds_symbol}
                alt="Birds"
              />
            ),
            width: 40,
          },
          {
            title: t('treatment-search|table.bees'),
            key: 'bees',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.bees_symbol}
                alt="Bees"
              />
            ),
            width: 40,
          },
        ],
      },
      {
        title: t('treatment-search|table.fate_and_behaviour'),
        children: [
          {
            title: t('treatment-search|table.persistence'),
            key: 'persistence',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.persistence_symbol}
                alt="Persistence"
              />
            ),
            width: 40,
          },
          {
            title: t('treatment-search|table.leaching_potential'),
            key: 'leaching_potential',
            className: 'vertical-cell',
            render: (_, record) => (
              <RiskSymbolCell
                symbolData={record.symbols?.environment?.leaching_symbol}
                alt="Leaching potential"
              />
            ),
            width: 40,
          },
        ],
      },
    ],
  },
  {
    title: t('treatment-search|table.details'),
    key: 'details',
    className: 'vertical-cell',
    rowSpan: 3,
    render: (_, record) => (
      <ActionLinkCell
        href={`/traitement/details/${record.id}`}
        target="_blank"
        className="btn btn--primary btn--sm"
      >
        {t('treatment-search|table.see')}
      </ActionLinkCell>
    ),
    width: 40,
  },
];
};
